import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BatteryFull,
  QrCode,
  Trash,
  Printer,
  CheckCircle,
  Warning,
  X,
  Plus,
  NotePencil,
  WarningOctagon,
  Wrench,
  ChatText,
  Storefront,
  User,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import QRScanner from "../components/QRScanner";
import { toast } from "sonner";
import { formatDocument } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const DEFAULT_CHECKLIST_LABELS = {
  motor_instalado: "Motor instalado",
  freios_verificados: "Freios verificados",
  luzes_funcionando: "Luzes funcionando",
  painel_conectado: "Painel conectado",
  acelerador_testado: "Acelerador testado",
  pneus_calibrados: "Pneus calibrados",
  bateria_fixada: "Baterias fixadas",
  carenagem_fechada: "Carenagem fechada",
};

const OBS_TYPES = [
  {
    value: "observation",
    label: "Observação",
    icon: ChatText,
    color: "text-blue-500 bg-blue-500/10",
  },
  {
    value: "issue",
    label: "Avaria",
    icon: WarningOctagon,
    color: "text-[#FF3B30] bg-[#FF3B30]/10",
  },
  {
    value: "repair",
    label: "Reparo",
    icon: Wrench,
    color: "text-yellow-500 bg-yellow-500/10",
  },
];

export default function MotorcycleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moto, setMoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [observations, setObservations] = useState([]);
  const [checklistLabels, setChecklistLabels] = useState(
    DEFAULT_CHECKLIST_LABELS,
  );
  const [showObsForm, setShowObsForm] = useState(false);
  const [obsText, setObsText] = useState("");
  const [obsType, setObsType] = useState("observation");
  const [savingObs, setSavingObs] = useState(false);

  const fetchMoto = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/motorcycles/${id}`, {
        withCredentials: true,
      });
      setMoto(response.data);

      // If moto has a checklist template, fetch labels
      if (response.data.checklist_template_id) {
        try {
          const templateRes = await axios.get(
            `${API}/checklist-templates/${response.data.checklist_template_id}`,
            { withCredentials: true },
          );
          const labels = {};
          Object.entries(templateRes.data.items).forEach(([key, item]) => {
            labels[key] = item.label;
          });
          setChecklistLabels(labels);
        } catch (err) {
          console.error("Error fetching template:", err);
        }
      }
    } catch (error) {
      toast.error("Erro ao carregar dados da moto");
      navigate("/motos");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchObservations = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/motorcycles/${id}/observations`,
        { withCredentials: true },
      );
      setObservations(response.data);
    } catch (error) {
      console.error("Error fetching observations:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchMoto();
    fetchObservations();
  }, [fetchMoto, fetchObservations]);

  const handleScanSuccess = async (batterySN) => {
    try {
      await axios.post(
        `${API}/motorcycles/${id}/batteries`,
        {
          battery_sn: batterySN,
        },
        { withCredentials: true },
      );

      toast.success(`Bateria ${batterySN} vinculada com sucesso!`);
      setShowScanner(false);
      fetchMoto();
    } catch (error) {
      const message =
        error.response?.data?.detail || "Erro ao vincular bateria";
      toast.error(message);
    }
  };

  const handleRemoveBattery = (batterySN) => {
    toast("Remover bateria?", {
      description: `Tem certeza que deseja remover a bateria ${batterySN}?`,
      action: {
        label: "Sim, remover",
        onClick: async () => {
          try {
            await axios.delete(`${API}/motorcycles/${id}/batteries/${batterySN}`, {
              withCredentials: true,
            });
            toast.success("Bateria removida com sucesso!");
            fetchMoto();
          } catch (error) {
            toast.error("Erro ao remover bateria");
          }
        },
      },
      cancel: {
        label: "Cancelar",
      },
      duration: 5000,
    });
  };

  const handleChecklistChange = async (item, checked) => {
    try {
      await axios.put(
        `${API}/motorcycles/${id}/checklist`,
        {
          item,
          checked,
        },
        { withCredentials: true },
      );
      fetchMoto();
    } catch (error) {
      toast.error("Erro ao atualizar checklist");
    }
  };

  const handleFinalize = async () => {
    try {
      await axios.post(
        `${API}/motorcycles/${id}/finalize`,
        {},
        { withCredentials: true },
      );
      toast.success("Moto finalizada com sucesso!");
      fetchMoto();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao finalizar moto";
      toast.error(message);
    }
  };

  const handleAddObservation = async (e) => {
    e.preventDefault();
    if (!obsText.trim()) return;

    setSavingObs(true);
    try {
      await axios.post(
        `${API}/motorcycles/${id}/observations`,
        {
          text: obsText,
          type: obsType,
        },
        { withCredentials: true },
      );

      toast.success("Registro adicionado com sucesso!");
      setObsText("");
      setObsType("observation");
      setShowObsForm(false);
      fetchObservations();
    } catch (error) {
      toast.error("Erro ao adicionar registro");
    } finally {
      setSavingObs(false);
    }
  };

  const requiredBatteries = moto ? (moto.voltage === 60 ? 5 : 6) : 0;
  const allBatteriesLinked = moto
    ? moto.batteries.length >= requiredBatteries
    : false;
  const allChecklistDone = moto
    ? Object.values(moto.checklist).every((v) => v)
    : false;
  const canFinalize =
    allBatteriesLinked && allChecklistDone && moto?.status !== "completed";

  if (loading) {
    return (
      <Layout title="Carregando..." showBack>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!moto) return null;

  return (
    <Layout title={moto.chassis_sn} showBack backTo="/motos">
      <div className="space-y-4 pb-6">
        {/* Status Header */}
        <div
          className={`card p-4 sm:p-5 ${moto.status === "completed" ? "border-[#00E676]/50 shadow-[0_0_15px_rgba(0,230,118,0.1)]" : ""}`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-[#A1A1AA]">Modelo</p>
              <p className="text-base sm:text-lg font-bold truncate">
                {moto.model}
              </p>
            </div>
            <div className="sm:text-right shrink-0 border-t border-white/5 sm:border-0 pt-2 sm:pt-0">
              <p className="text-xs sm:text-sm text-[#A1A1AA]">Voltagem</p>
              <p className="text-base sm:text-lg font-bold">{moto.voltage}V</p>
            </div>
          </div>

          {/* Store and Technician Info */}
          {(moto.store_name || moto.technician_name) && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {moto.store_name && (
                <div className="flex items-center gap-2 min-w-0">
                  <Storefront size={18} className="text-purple-500 shrink-0" />
                  <span className="text-xs sm:text-sm text-[#A1A1AA] truncate">
                    {moto.store_name}
                  </span>
                </div>
              )}
              {moto.technician_name && (
                <div className="flex items-center gap-2 min-w-0">
                  <User size={18} className="text-blue-500 shrink-0" />
                  <span className="text-xs sm:text-sm text-[#A1A1AA] truncate">
                    {moto.technician_name}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              {moto.status === "completed" ? (
                <>
                  <CheckCircle
                    size={20}
                    weight="fill"
                    className="text-[#00E676]"
                  />
                  <span className="text-[#00E676] font-medium text-sm sm:text-base">
                    Finalizada
                  </span>
                </>
              ) : moto.status === "assembly" ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-yellow-500 shrink-0"></div>
                  <span className="text-yellow-500 font-medium text-sm sm:text-base">
                    Em Montagem
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full bg-[#A1A1AA] shrink-0"></div>
                  <span className="text-[#A1A1AA] font-medium text-sm sm:text-base">
                    Pendente
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Client Section */}
        {moto.client && (
          <div className="mt-6">
            <div className="bg-[#00E676]/10 border border-[#00E676]/20 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <User size={18} className="text-[#00E676]" />
                  <p className="text-xs sm:text-sm text-[#00E676] font-medium uppercase tracking-wider">
                    Cliente Vinculado
                  </p>
                </div>
                <p className="font-bold text-lg sm:text-xl text-white truncate">
                  {moto.client.name}
                </p>
                {moto.client.document && (
                  <p className="text-sm text-[#A1A1AA] font-mono mt-1">
                    CPF/CNPJ: {formatDocument(moto.client.document)}
                  </p>
                )}
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#00E676]/20 items-center justify-center shrink-0">
                <User size={24} className="text-[#00E676]" weight="fill" />
              </div>
            </div>
          </div>
        )}

        {/* Batteries Section */}
        <div className="card p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BatteryFull size={24} className="text-[#00E676]" />
              <h2 className="font-bold text-sm sm:text-base">Baterias</h2>
            </div>
            <span
              className={`text-sm sm:text-base font-mono ${allBatteriesLinked ? "text-[#00E676]" : "text-[#A1A1AA]"}`}
            >
              {moto.batteries.length}/{requiredBatteries}
            </span>
          </div>

          {/* Battery Slots */}
          <div className="space-y-2">
            {Array.from({ length: requiredBatteries }).map((_, index) => {
              const battery = moto.batteries[index];
              return (
                <div
                  key={index}
                  className={`battery-slot p-3 sm:p-4 ${battery ? "linked" : ""}`}
                  data-testid={`battery-slot-${index}`}
                >
                  {battery ? (
                    <div className="flex items-center justify-between w-full gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle
                          size={20}
                          weight="fill"
                          className="text-[#00E676] shrink-0"
                        />
                        <span className="font-mono text-[11px] sm:text-sm truncate sm:break-normal break-all leading-tight">
                          {battery}
                        </span>
                      </div>
                      {moto.status !== "completed" && (
                        <button
                          onClick={() => handleRemoveBattery(battery)}
                          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg hover:bg-[#FF3B30]/20 transition-colors shrink-0"
                          data-testid={`remove-battery-${index}`}
                        >
                          <Trash size={18} className="text-[#FF3B30]" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-[#A1A1AA] text-xs sm:text-sm">
                      Slot {index + 1} - Vazio
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Scan Button */}
          {moto.status !== "completed" && !allBatteriesLinked && (
            <button
              onClick={() => setShowScanner(true)}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2 py-3"
              data-testid="scan-battery-button"
            >
              <QrCode size={20} />
              Escanear Bateria
            </button>
          )}
        </div>

        {/* Checklist Section - Sim/Não */}
        <div className="card p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-sm sm:text-base">
              Checklist de Montagem
            </h2>
            <span
              className={`text-sm sm:text-base ${allChecklistDone ? "text-[#00E676]" : "text-[#A1A1AA]"}`}
            >
              {Object.values(moto.checklist).filter((v) => v).length}/
              {Object.keys(moto.checklist).length}
            </span>
          </div>

          <div className="space-y-2">
            {Object.entries(moto.checklist).map(([key, checked]) => (
              <div
                key={key}
                className="bg-[#1A1A1A] rounded-lg p-3 border border-transparent hover:border-white/5 transition-colors"
                data-testid={`checklist-${key}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span className="text-sm font-medium flex-1 pr-2 leading-snug">
                    {checklistLabels[key] || key}
                  </span>
                  <div className="flex items-center gap-2 self-end sm:self-auto w-full sm:w-auto">
                    <button
                      onClick={() =>
                        moto.status !== "completed" &&
                        handleChecklistChange(key, false)
                      }
                      disabled={moto.status === "completed"}
                      className={`flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        !checked
                          ? "bg-[#FF3B30] text-white"
                          : "bg-[#252525] text-[#A1A1AA] hover:bg-[#303030]"
                      } ${moto.status === "completed" ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      NÃO
                    </button>
                    <button
                      onClick={() =>
                        moto.status !== "completed" &&
                        handleChecklistChange(key, true)
                      }
                      disabled={moto.status === "completed"}
                      className={`flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        checked
                          ? "bg-[#00E676] text-black"
                          : "bg-[#252525] text-[#A1A1AA] hover:bg-[#303030]"
                      } ${moto.status === "completed" ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      SIM
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Observations/Issues Section */}
        <div className="card p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 min-w-0 pr-2">
              <NotePencil size={24} className="text-[#00E676] shrink-0" />
              <h2 className="font-bold text-sm sm:text-base truncate">
                Histórico de Avarias
              </h2>
            </div>
            <button
              onClick={() => setShowObsForm(!showObsForm)}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#00E676] flex items-center justify-center shrink-0 hover:bg-[#00C853] transition-colors"
              data-testid="add-observation-button"
            >
              <Plus
                size={20}
                weight="bold"
                className="text-black sm:w-6 sm:h-6"
              />
            </button>
          </div>

          {/* Add Observation Form */}
          {showObsForm && (
            <form
              onSubmit={handleAddObservation}
              className="mb-4 p-4 bg-[#1A1A1A] rounded-lg space-y-4 border border-[#00E676]/20"
            >
              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-2">
                  Tipo de Registro
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {OBS_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setObsType(type.value)}
                        className={`p-2 sm:p-3 rounded-lg flex flex-col items-center justify-center gap-1 sm:gap-1.5 transition-colors border ${
                          obsType === type.value
                            ? type.color + " border-current"
                            : "bg-[#252525] text-[#A1A1AA] border-transparent"
                        }`}
                      >
                        <Icon size={20} className="sm:w-6 sm:h-6" />
                        <span className="text-[10px] sm:text-xs text-center leading-tight">
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-2">
                  Descrição
                </label>
                <textarea
                  value={obsText}
                  onChange={(e) => setObsText(e.target.value)}
                  className="w-full bg-[#252525] border border-white/10 rounded-lg p-3 text-sm text-white resize-none h-24 focus:outline-none focus:border-[#00E676] transition-colors"
                  placeholder="Descreva a avaria, observação ou reparo..."
                  data-testid="observation-text-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowObsForm(false);
                    setObsText("");
                  }}
                  className="btn-secondary w-full sm:flex-1 h-12"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!obsText.trim() || savingObs}
                  className="btn-primary w-full sm:flex-1 h-12"
                  data-testid="save-observation-button"
                >
                  {savingObs ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          )}

          {/* Observations List */}
          {observations.length === 0 ? (
            <p className="text-[#A1A1AA] text-xs sm:text-sm text-center py-4 bg-[#1A1A1A] rounded-lg border border-dashed border-white/10">
              Nenhum registro de avaria ou observação
            </p>
          ) : (
            <div className="space-y-3">
              {observations.map((obs, idx) => {
                const obsTypeConfig =
                  OBS_TYPES.find((t) => t.value === obs.type) || OBS_TYPES[0];
                const Icon = obsTypeConfig.icon;
                return (
                  <div
                    key={idx}
                    className="bg-[#1A1A1A] rounded-lg p-3 sm:p-4 border border-white/5"
                    data-testid={`observation-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${obsTypeConfig.color}`}
                      >
                        <Icon size={16} className="sm:w-5 sm:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span
                            className={`text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded ${obsTypeConfig.color}`}
                          >
                            {obsTypeConfig.label}
                          </span>
                          <span className="text-[10px] sm:text-xs text-[#A1A1AA] whitespace-nowrap">
                            {new Date(obs.created_at).toLocaleDateString(
                              "pt-BR",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm leading-relaxed">
                          {obs.text}
                        </p>
                        <p className="text-[10px] sm:text-xs text-[#A1A1AA] mt-2 font-medium">
                          Por: {obs.user_name}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        {moto.status !== "completed" && (
          <div className="space-y-3">
            {!canFinalize && (
              <div className="card p-4 border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                  <Warning
                    size={20}
                    className="text-yellow-500 flex-shrink-0 mt-0.5"
                  />
                  <div className="text-xs sm:text-sm min-w-0">
                    <p className="font-bold text-yellow-500 mb-2">
                      Para finalizar a montagem:
                    </p>
                    <ul className="text-[#A1A1AA] space-y-1.5 ml-1">
                      {!allBatteriesLinked && (
                        <li className="break-words">
                          • Vincule todas as {requiredBatteries} baterias
                        </li>
                      )}
                      {!allChecklistDone && (
                        <li className="break-words">
                          • Complete todos os itens do checklist
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleFinalize}
              disabled={!canFinalize}
              className="btn-primary w-full py-3 sm:py-4 text-sm sm:text-base font-bold shadow-lg"
              data-testid="finalize-button"
            >
              Finalizar Montagem
            </button>
          </div>
        )}

        {/* QR Code for completed */}
        {moto.status === "completed" && (
          <div className="space-y-3 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 sm:space-y-0">
            <button
              onClick={() => setShowQRCode(true)}
              className="btn-secondary w-full flex items-center justify-center gap-2 py-3"
              data-testid="view-qrcode-button"
            >
              <QrCode size={20} />
              Ver QR Code
            </button>
            <button
              onClick={() => {
                setShowQRCode(true);
                setTimeout(() => window.print(), 500);
              }}
              className="btn-secondary w-full flex items-center justify-center gap-2 py-3 bg-[#1A1A1A] hover:bg-[#252525] border border-white/20"
              data-testid="print-label-button"
            >
              <Printer size={20} />
              Imprimir Etiqueta
            </button>
          </div>
        )}
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onScan={handleScanSuccess}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-sm shadow-2xl border border-white/10 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">QR Code da Moto</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors no-print"
                data-testid="close-qrcode-modal"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <div className="print-label bg-white p-5 rounded-xl flex flex-col items-center shadow-inner">
              <img
                src={`${API}/motorcycles/${id}/qrcode`}
                alt="QR Code"
                className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
                data-testid="qrcode-image"
              />
              <p className="chassis-text mt-3 text-black font-mono text-sm sm:text-base font-bold text-center break-all">
                {moto.chassis_sn}
              </p>
              <p className="text-[11px] sm:text-xs text-gray-600 mt-1 font-medium text-center">
                {moto.model} - {moto.voltage}V
              </p>
            </div>

            <p className="text-[11px] sm:text-xs text-[#A1A1AA] text-center mt-5 font-medium no-print">
              Escaneie para rastrear esta moto no sistema
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
}
